import { Box, Button, Divider, Typography } from '@mui/material';
import * as React from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { ReactComponent as DragControlIcon } from '../../../assets/images/icon-drag-control.svg';
import { CAMPAIGN_ROW_HEIGHT_COMPACT } from '../../../constants/campaigns';
import { useCurrency } from '../../../contexts/CurrencyProvider';
import { IOneLinkChunkFormProps, TOLInventoryInfo } from '../../../contexts/OneLinkSummaryProvider';
import { ICampaign, ICampaignItem, ICampaignSummary } from '../../../types/campaigns';
import { IInventoryItem, InventoryTypesEnum, IOnDemandItem } from '../../../types/inventories';
import { OneLinkStatusEnum } from '../../../types/oneLink';
import { BoxDetailsSidebarModeEnum } from '../../../types/shell';
import { getCanRemoveItemFromCampaign, getIsItemLastVisibleInCampaign } from '../../../utils/campaigns';
import { isObjectsEqual } from '../../../utils/helpers';
import { mapItemFromState, mapMskuItemToRequest } from '../../../utils/inventories';
import DraggableList from '../../DraggableList/DraggableList';
import { BoxDetailsItem, Price } from '../../index';
import { FormControls } from '../../SummaryComponentMUI/components/common';
import { AddCampaign, AddCampaigns, AddInventoryItems, CampaignListItem } from '../../_mui';
import { actions, reducer } from './reducer';

import variables from '../../../contexts/ThemeProvider/variables';

enum SidebarMode {
  AddCampaign = 'add-campaign',
  AddCampaigns = 'add-campaigns',
  AddItems = 'add-items',
}

const InventoryForm = forwardRef(({ onSubmit, instance, onClose }: IOneLinkChunkFormProps, ref: any) => {
  const { getCampaignTotal } = useCurrency();
  const [sidebarMode, setSidebarMode] = useState<SidebarMode | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const initialReducerState = {
    pick_campaign_ids: instance?.pick_campaign_ids || null,
    pick_campaigns: instance?.pick_campaigns || undefined,
    items: instance?.items || undefined,
    pre_created_engagement_id: instance?.pre_created_engagement_id || '',
    campaign_name: instance?.campaign_name || '',
  };

  const [state, dispatch] = useReducer(reducer, initialReducerState);
  const { items, pre_created_engagement_id, campaign_name, pick_campaigns, pick_campaign_ids } = state;

  const isPYG = useMemo(() => pick_campaign_ids && pick_campaign_ids.length > 0, [pick_campaign_ids]);
  const isSingleCampaign = useMemo(() => pick_campaigns && pick_campaigns.length === 1, [pick_campaigns]);
  const isCustomSend = useMemo(() => !isPYG && !isSingleCampaign, [isPYG, isSingleCampaign]);
  const canRemoveAll = useMemo(() => false, []);
  const isOneLinkActive = useMemo(() => instance?.status === OneLinkStatusEnum.Active, [instance?.status]);
  const isCampaignReorderable = useMemo(
    () => !!isPYG && !!pick_campaigns && pick_campaigns.length >= 2 && isOneLinkActive,
    [isPYG, pick_campaigns, isOneLinkActive],
  );

  const getCampaignId = useCallback((campaign: ICampaignSummary) => campaign.box_id, []);

  const boxDisplayedPrice = React.useMemo(() => {
    if (items) {
      return getCampaignTotal({ items });
    }
  }, [getCampaignTotal, items]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e?.preventDefault?.();

      const preparedItems = items?.map((item) =>
        item.type === InventoryTypesEnum.MSKU ? mapMskuItemToRequest(item) : item,
      );

      const inventory: TOLInventoryInfo = isPYG
        ? {
            pick_campaign_ids: pick_campaign_ids as string[],
            items: null,
            pre_created_engagement_id: null,
            campaign_name: null,
          }
        : {
            campaign_name,
            items: preparedItems as IInventoryItem[],
            pre_created_engagement_id: pre_created_engagement_id as string,
            pick_campaign_ids: null,
          };

      onSubmit({ inventory_info: inventory });

      setIsDirty(false);
      ref.current = false;
    },
    [isPYG, pre_created_engagement_id, campaign_name, items, pick_campaign_ids, onSubmit],
  );

  const handleChangeCampaign = useCallback(() => {
    setSidebarMode(SidebarMode.AddCampaign);
  }, []);

  const header = useMemo(() => {
    let title;
    let text;

    switch (true) {
      case !!campaign_name:
        title = campaign_name;
        text = 'These items are included in this campaign.';
        break;
      case isPYG:
        title = 'Campaign Options';
        text = 'The recipient can select from these campaigns.';
        break;
      default:
        title = 'Custom send';
        text = 'These items are included in send.';
        break;
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          {text && <Typography variant="body2">{text}</Typography>}
        </Box>
        {!isPYG && (
          <Box>
            <Button
              variant="text"
              color="primary"
              onClick={handleChangeCampaign}
              sx={{
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              Change campaign
            </Button>
          </Box>
        )}
      </Box>
    );
  }, [isPYG, campaign_name]);

  const currentItems = useMemo(
    () => state.items && state.items.length > 0 && mapItemFromState(state.items as ICampaignItem[]),
    [state.items],
  );

  const initialItems = useMemo(() => {
    return (
      initialReducerState.items && initialReducerState.items.length > 0 && mapItemFromState(initialReducerState.items)
    );
  }, [initialReducerState?.items]);

  const hasSameItems = useMemo(() => isObjectsEqual(currentItems, initialItems), [currentItems, initialItems]);

  const hasSameCampaigns = useMemo(() => {
    return (
      initialReducerState?.pick_campaign_ids &&
      state.pick_campaign_ids &&
      isObjectsEqual(initialReducerState?.pick_campaign_ids, state.pick_campaign_ids)
    );
  }, [initialReducerState?.pick_campaign_ids, state.pick_campaign_ids]);

  const isItems = useMemo(() => state.items && state.items?.length > 0, [state.items]);
  const isCampaigns = useMemo(
    () => state.pick_campaign_ids && state.pick_campaign_ids.length > 0,
    [state.pick_campaign_ids],
  );

  const handleRemoveItem = useCallback((id: string) => {
    dispatch(actions.removeItem(id));
  }, []);

  const handleAddItems = useCallback((itemsList: IInventoryItem[]) => {
    dispatch(actions.addItems(itemsList));
  }, []);

  const handleRemoveCampaign = useCallback((id: string) => {
    dispatch(actions.removeCampaign(id));
  }, []);

  const handleAddCampaigns = useCallback((campaigns: ICampaignSummary[]) => {
    dispatch(actions.addCampaigns(campaigns));
  }, []);

  const handleAddSingleCampaign = useCallback((campaign: ICampaignSummary) => {
    dispatch(actions.addCampaign(campaign));
  }, []);

  const handleReorderCampaigns = useCallback((nextOrder: ICampaignSummary[]) => {
    dispatch(actions.reorderCampaigns(nextOrder));
  }, []);

  const itemMarkup = useCallback(
    (item: ICampaignItem | IOnDemandItem, itemsList: ICampaignItem[] | IOnDemandItem[]) => {
      const { item_id } = item;
      const isLastVisibleItem = getIsItemLastVisibleInCampaign(itemsList, item_id);
      const isCustomizableItem = item.item_customization_allowed;
      const canRemoveItem = canRemoveAll
        ? getCanRemoveItemFromCampaign(itemsList, item.item_id)
        : itemsList?.length > 1 && getCanRemoveItemFromCampaign(itemsList, item.item_id);

      return (
        <Box key={item_id} sx={{ marginX: 0.5 }}>
          <BoxDetailsItem
            item={item as ICampaignItem | IOnDemandItem}
            canHide={!isLastVisibleItem && !isCustomizableItem}
            key={item.item_id}
            mode={BoxDetailsSidebarModeEnum.Edit}
            onChange={(payload) => dispatch(actions.replaceQuantity(payload))}
            onRemove={canRemoveItem ? ({ item_id: id }) => handleRemoveItem(id) : undefined}
          />
        </Box>
      );
    },
    [handleRemoveItem, canRemoveAll],
  );

  const campaignItemMarkup = useCallback(
    (campaign: ICampaign) => {
      const total = getCampaignTotal(campaign);
      const { box_id: boxId } = campaign;

      return (
        <CampaignListItem campaign={campaign} key={boxId} total={total} onRemove={() => handleRemoveCampaign(boxId)} />
      );
    },
    [handleRemoveCampaign, getCampaignTotal],
  );

  const renderItem = useCallback(
    (item: ICampaignItem | IOnDemandItem | ICampaign) => {
      if (isCustomSend || isSingleCampaign) {
        return itemMarkup(item as IOnDemandItem | ICampaignItem, (items as ICampaignItem[]) || []);
      }
      return campaignItemMarkup(item as ICampaign);
    },
    [isSingleCampaign, isCustomSend, items, itemMarkup, campaignItemMarkup],
  );

  const handleModeButtonAction = useCallback(() => {
    return !!isPYG ? setSidebarMode(SidebarMode.AddCampaign) : setSidebarMode(SidebarMode.AddCampaigns);
  }, [isPYG]);

  const handleAddButtonAction = useCallback(() => {
    return !!isPYG ? setSidebarMode(SidebarMode.AddCampaigns) : setSidebarMode(SidebarMode.AddItems);
  }, [isPYG]);

  const renderList = useCallback(() => {
    if (isCampaignReorderable && pick_campaigns) {
      return (
        <DraggableList<ICampaignSummary>
          items={pick_campaigns}
          getId={getCampaignId}
          rowHeight={CAMPAIGN_ROW_HEIGHT_COMPACT}
          isBounded={false}
          onDragStop={handleReorderCampaigns}
          render={pick_campaigns.map((campaign) => (
            <Box
              key={campaign.box_id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <Box component={DragControlIcon} tabIndex={-1} sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>{campaignItemMarkup(campaign as ICampaign)}</Box>
            </Box>
          ))}
        />
      );
    }
    const list = (pick_campaign_ids?.length ? pick_campaigns : items) as
      | (ICampaignItem | IOnDemandItem | ICampaign)[]
      | undefined;
    return list?.map(renderItem);
  }, [
    pick_campaign_ids,
    pick_campaigns,
    items,
    renderItem,
    isCampaignReorderable,
    campaignItemMarkup,
    handleReorderCampaigns,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
    setIsDirty(false);
    ref.current = false;
  }, [onClose]);

  useEffect(() => {
    const itemsChanged = !hasSameItems;
    const campaignsChanged = !hasSameCampaigns;

    let dirty: boolean;

    switch (true) {
      case isCampaigns:
        dirty = campaignsChanged;
        break;
      case isItems:
        dirty = itemsChanged;
        break;
      default:
        dirty = itemsChanged || campaignsChanged;
        break;
    }

    ref.current = dirty;
    setIsDirty(dirty);
  }, [
    isItems,
    isCampaigns,
    currentItems,
    initialItems,
    state.pick_campaign_ids,
    initialReducerState.pick_campaign_ids,
  ]);

  return (
    <Box sx={{ height: '100%' }}>
      {sidebarMode === SidebarMode.AddCampaign && (
        <AddCampaign onClose={() => setSidebarMode(null)} onSelect={handleAddSingleCampaign} />
      )}
      {sidebarMode === SidebarMode.AddCampaigns && (
        <AddCampaigns onClose={() => setSidebarMode(null)} value={pick_campaigns} onSubmit={handleAddCampaigns} />
      )}
      {sidebarMode === SidebarMode.AddItems && (
        <AddInventoryItems
          onClose={() => setSidebarMode(null)}
          items={items as IInventoryItem[]}
          onSubmit={handleAddItems}
        />
      )}
      <Box
        component="form"
        role="form"
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
        onSubmit={handleSubmit}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" sx={{ flexGrow: 1 }} onClick={handleModeButtonAction}>
              {!!isPYG ? 'Convert to single campaign' : 'Convert to pick your gift'}
            </Button>
            <Button variant="contained" sx={{ color: variables.white }} onClick={handleAddButtonAction}>
              {!!isPYG ? 'Add campaign' : 'Add items'}
            </Button>
          </Box>
          <Divider orientation="horizontal" flexItem />
          {header}
        </Box>
        <Box
          sx={{
            flex: '1 1 0',
            minHeight: 0,
            overflow: 'auto',
            mb: 2,
          }}
        >
          {renderList()}
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          {!isPYG && (
            <Box sx={{ m: 2, display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
              <Typography sx={{ color: variables.grayEbony }}>Total cost</Typography>
              <Price value={boxDisplayedPrice!} big />
            </Box>
          )}
          <FormControls onCancel={handleCancel} isDisabled={!isDirty} />
        </Box>
      </Box>
    </Box>
  );
});

export default InventoryForm;
